(ns dashboard.views.questionnaire.question-lab.view
  (:require [clojure.string :as str]
            [dashboard.common :refer [format-date format-score]]
            [dashboard.components.answer :as answer]
            [dashboard.components.expanding-textarea :refer [expanding-textarea]]
            [dashboard.components.keybindings :refer [keybindings]]
            [dashboard.components.panel :as panel]
            [dashboard.components.questionnaire.attachments-button :refer [attachments-button]]
            [dashboard.components.questionnaire.notes-button :refer [notes-button]]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as applied-structured-analysis]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.entity.question.answer-scope :as answer-scope]
            [dashboard.entity.question.question-status :as question-status]
            [dashboard.entity.questionnaire.db :as questionnaire.db]
            [dashboard.html :as html]
            [dashboard.notifications :as notifications]
            [dashboard.services.answers :as answers]
            [dashboard.services.document-list :as document-list]
            [dashboard.services.questionnaires :as questionnaires]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [dashboard.views.questionnaire.question-lab.common :as common]
            [dashboard.views.questionnaire.question-lab.events :as events]
            [dashboard.views.questionnaire.question-lab.subs :as subs]
            [dashboard.views.questionnaire.question-list.config :as question-list-config]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]))

(defn question-index [questionnaire-id]
  (let [index @(rf/subscribe [::subs/question-index questionnaire-id])
        total (count @(rf/subscribe [::subs/question-ids questionnaire-id]))]
    [:div.columns.is-size-5.is-vcentered.m-0
     [:div.column.is-narrow
      [:button.button.ml-4.is-secondary
       {:disabled (<= index 0)
        :on-click (fn [_]
                    (rf/dispatch [::events/set-question-index questionnaire-id (dec index) true]))
        :title "Jump to previous question, as per order of questions filtered"}
       [:span.icon>i.fas.fa-caret-left.is-size-3]]]
     [:div.column.has-text-centered
      [:span "Question"]
      [:span.ml-2.has-text-weight-bold
       (+ index 1)]
      [:span.ml-2 "of"]
      [:span.ml-2.has-text-weight-bold total]]
     [:div.column.is-narrow
      [:button.button.mr-4.is-secondary
       {:disabled (>= index (- total 1))
        :on-click (fn [_]
                    (rf/dispatch [::events/set-question-index questionnaire-id (inc index) true]))
        :title "Jump to next question, as per order of questions filtered"}
       [:span.icon>i.fas.fa-caret-right.is-size-3]]]]))

(defn quick-buttons [questionnaire-id]
  (let [buttons [[answer/yes-class "Yes"]
                 [answer/no-class "No"]
                 [answer/warning-class "N/A"]]
        {:keys [response]} @(rf/subscribe [::subs/edit-answer-state questionnaire-id])
        response-is-quick-answer? (some (fn [[_ value]]
                                          (= value response))
                                        buttons)]
    [:div.field.is-grouped.mt-4
     (map (fn [[class value]]
            ^{:key value}
            [:p.control
             [:button.button.is-rounded.is-small
              {:disabled (not (or response-is-quick-answer?
                                  (str/blank? response)))
               :class class
               :on-click (fn [_e]
                           (rf/dispatch [::events/set-edit-answer-state-field questionnaire-id :response value]))
               :title (str "Click to quickly set the response to " value)}
              value]])
          buttons)]))

(defn maximum-depth
  "Find the maximum depth of the question tree."
  [index question]
  (let [parent-id (get question :parent-id)
        parent (first (get index parent-id))]
    (if parent-id
      (inc (maximum-depth index parent))
      0)))

(defn question-element
  ([index question]
   (let [max-depth (maximum-depth index question)]
     (question-element index question max-depth max-depth)))
  ([index {:keys [parent-id content display-name]} depth max-depth]
   [:<>
    (when parent-id
      [question-element index (first (get index parent-id)) (dec depth) max-depth])
    [:div (when (> depth 1) {:style {:margin-left (str (* depth 0.75) "rem")}})
     (when parent-id
       [:div.mr-2.branch-icon])
     [:span {:class (let [inv-depth (- max-depth depth)]
                      (if (zero? inv-depth)
                        ["has-text-weight-bold" "is-size-5"]
                        [(str "is-size-" (max 7 (+ 5 inv-depth))) "has-text-grey-light"]))}
      (when display-name
        (str display-name " - "))
      (html/as-hiccup-with-anchors content)]]]))

(defn question-content [questionnaire-id]
  (let [current-question @(rf/subscribe [::subs/current-question questionnaire-id])
        index @(rf/subscribe [::subs/questions-by-id questionnaire-id])]
    [:div
     {:key questionnaire-id}
     [:b (html/as-hiccup-with-anchors (:section-name current-question))]
     [question-element index current-question]]))

(defn answer-options [options questionnaire-id]
  (let [edit-state @(rf/subscribe [::subs/edit-answer-state questionnaire-id])
        {:keys [radio-buttons checkboxes]} (group-by #(if (:multi %) :checkboxes :radio-buttons) options)
        {:keys [single-select-options multi-select-options]} edit-state]
    [:form.my-4
     (when (seq checkboxes)
       [:fieldset
        [:legend.has-text-weight-bold "Multi select options:"]
        (map (fn [{:keys [id content selected]}]
               (let [checked (if edit-state
                               (contains? multi-select-options id)
                               selected)]
                 [:p {:key (str id)}
                  [:input {:id (str id)
                           :type "checkbox"
                           :disabled (nil? edit-state)
                           :checked checked
                           :on-change #(events/update-edit-answer-state-field
                                        questionnaire-id
                                        :multi-select-options
                                        (fnil (if checked disj conj) #{})
                                        id)}]
                  [:label.ml-2
                   {:for (str id)}
                   (html/as-hiccup-with-anchors content)]]))
             checkboxes)])
     (when (seq radio-buttons)
       [:fieldset
        [:legend.has-text-weight-bold "Single select options:"]
        (map (fn [{:keys [id content selected]}]
               [:p {:key (str id)}
                [:input {:id (str id)
                         :type "radio"
                         :disabled (nil? edit-state)
                         :checked (if edit-state
                                    (contains? single-select-options id)
                                    selected)
                         :on-change (fn [_]
                                      (rf/dispatch [::events/set-edit-answer-state-field questionnaire-id :single-select-options #{id}]))}]
                [:label.ml-2
                 {:for (str id)}
                 (html/as-hiccup-with-anchors content)]])
             radio-buttons)])]))

(def analysis-divider
  [:div.mt-2.mb-2
   {:style {:border-top-color "rgb(219, 219, 219)"
            :border-top-style "solid"
            :border-top-width "1.5px"}}])

(defn LinkedAnalyses [questionnaire-id analysis-pred applied-analysis-version-ids allow-unlinking? allow-browsing? current-question]
  (let [structured-analyses @(rf/subscribe [::subs/analyses-for-answer analysis-pred])]
    [:div
     [:hr.mt-0.mb-2]
     [:div.level.mb-2.p-2.has-background-highlight
      (if (seq structured-analyses)
        [:span>b "Linked analyses"]
        [:span.px-2 "No linked applied analyses"])
      (when allow-browsing?
        [:button.button.is-rounded.is-info.is-small
         {:on-click #(rf/dispatch [::events/show-browse-applied-analyses-modal
                                   applied-analysis-version-ids
                                   (fn [checked-analyses]
                                     (events/update-edit-answer-state-field
                                      questionnaire-id
                                      :applied-analysis-version-ids
                                      (fnil into #{})
                                      (mapv
                                       (comp :version-id first :applied-analyses)
                                       checked-analyses)))
                                   current-question])}
         [:span.icon>i.fas.fa-folder-tree]
         [:span "Browse applied analyses"]])]
     (when (seq structured-analyses)
       [:table {:style {:table-layout "auto"
                        :width "100%"
                        :border-collapse "collapse"}}
        [:tbody
         (for [{:keys [construct-id applied-analyses]
                :tree/keys [depth]
                :as structured-analysis} structured-analyses]
           (for [applied-analysis (->> (sort-by :applied-structured-analysis-id applied-analyses)
                                       ;; Drop versions that have not been linked, but keep all future versions, including ones that haven't been linked,
                                       ;; so that user can choose to link them to the question open in the lab
                                       (drop-while (fn [applied-analysis]
                                                     (not (analysis-pred applied-analysis)))))
                 :let [analysis-outdated? (:next-version-id applied-analysis)
                       {:keys [construct next-version-id states] :as _structured-analysis-of-applied-analysis} (:structured-analysis applied-analysis)
                       state-id->state-name (into {} states)]]
             [:<> {:key (:version-id applied-analysis)}
              [:tr {:class (when analysis-outdated?
                             "has-background-warning-light mx-2")
                    :style {:border (when (not (analysis-pred applied-analysis))
                                      "1px dashed")}
                    :title (let [titles (cond-> []
                                          (and (not (analysis-pred applied-analysis))
                                               analysis-outdated?)
                                          (conj "This analysis is outdated and not linked to this questions")


                                          (and (not (analysis-pred applied-analysis))
                                               (not analysis-outdated?))
                                          (conj (str "This applied analysis is an up-to-date newer version that is not linked to this answer."
                                                     (when allow-browsing?
                                                       "\n\nIt is shown so it can be linked to the current question open in the editor.")))

                                          ;; TODO: Can this be done by fetching the structured analysis via :structured-analysis-version-uuid, and then checking, if the structured analysis has a :next-version-id pointer
                                          (> (:created structured-analysis)
                                             (:created applied-analysis))
                                          (conj "Applied analysis was done for an older structured analysis"))]
                             (str/join "\n\n" titles))}
               [:td (when next-version-id
                      [:span.icon>i.fas.fa-exclamation-triangle {:title "The analysis points to an outdated structure"}])]
               [:td {:style {:width "99%"}}
                [:div {:style {:display "flex"
                               :align-items "center"}}
                 (when (> depth 0)
                   [:div.branch-icon])
                 [:div.is-flex
                  [:span (analysis.profile/format-analysis-id construct-id)]
                  [:span.ml-2 construct]]]]
               [:td
                [analysis.profile/state-tag {:class ["m-1"]} (state-id->state-name (:state-id applied-analysis))]]
               [:td
                [:div {:style {:display "flex"}}
                 [:button.button.is-rounded.is-circular.is-small.is-secondary.ml-2
                  {:title "View analysis"
                   :on-click #(rf/dispatch [::applied-structured-analysis/show-editor-modal construct-id :view applied-analysis])}
                  [:span.icon>i.fas.fa-file-lines]]
                 (when (or (analysis-pred applied-analysis)
                           (not analysis-outdated?))
                   (let [reasons-to-disable (cond-> []
                                              analysis-outdated?
                                              (conj "Linking outdated analysis not allowed")

                                              (contains? applied-analysis-version-ids (:version-id applied-analysis))
                                              (conj "Analysis already linked"))]
                     [:button.button.is-rounded.is-circular.is-small.is-secondary.ml-2
                      {:title  (if (empty? reasons-to-disable)
                                 "Add this analysis to current set of analyses in the answer editor state"
                                 (str/join "\n\n" reasons-to-disable))
                       :disabled (boolean (seq reasons-to-disable))
                       :on-click #(events/update-edit-answer-state-field questionnaire-id
                                                                         :applied-analysis-version-ids
                                                                         (fnil conj #{})
                                                                         (:version-id applied-analysis))}
                      [:span.icon>i.fas.fa-link]]))
                 (when (and allow-unlinking?
                            (analysis-pred applied-analysis))
                   [:button.button.is-rounded.is-circular.is-small.is-danger.ml-2
                    {:title "Unlink this analysis from editor state"
                     :on-click #(events/update-edit-answer-state-field questionnaire-id
                                                                       :applied-analysis-version-ids
                                                                       disj
                                                                       (:version-id applied-analysis))}
                    [:span.icon>i.fas.fa-unlink]])]]]]))]])]))

(defn AnalysisEditor [questionnaire-id current-question]
  (let [{:keys [applied-analysis-version-ids]} @(rf/subscribe [::subs/edit-answer-state questionnaire-id])
        analysis-pred (fn [applied-analysis]
                        (contains? applied-analysis-version-ids (:version-id applied-analysis)))]
    [:div.block.mt-0
     [LinkedAnalyses questionnaire-id
      ;; Note: Should get rid of passing a callback at render time, since re-creation of the callback leads to re-render
      analysis-pred
      applied-analysis-version-ids true true
      current-question]]))

(defn ResponseEditor [questionnaire-id]
  [:f> expanding-textarea
      {:style {:max-height "16rem"}
       :name        "response"
       :placeholder "Response..."
       :rows 1
       :default-value @(rf/subscribe [::subs/editor-field questionnaire-id :response])
       :on-change   (fn [e]
                      (rf/dispatch [::events/set-edit-answer-state-field questionnaire-id :response (.. e -target -value)]))}])

(defn CommentEditor [questionnaire-id]
  [:f> expanding-textarea
        {:class "mt-1"
         :style {:max-height "16rem"}
         :name        "comment"
         :placeholder "Comment..."
         :rows 1
         :default-value @(rf/subscribe [::subs/editor-field questionnaire-id :comment])
         :on-change   (fn [e]
                        (rf/dispatch [::events/set-edit-answer-state-field questionnaire-id :comment (.. e -target -value)]))}])

(defn UnstructuredAnalysisEditor [questionnaire-id]
  [:f> expanding-textarea
      {:style {:max-height "16rem"}
       :name "analysis"
       :placeholder "Unstructured analysis..."
       :rows 1
       :default-value @(rf/subscribe [::subs/editor-field questionnaire-id :analysis-content])
       :on-change (fn [e]
                    (rf/dispatch [::events/set-edit-answer-state-field questionnaire-id :analysis-content (.. e -target -value)]))}])

(defn answering-inputs [questionnaire-id current-question]
  (let [{:keys [comment-required _] :as question} current-question]
    [:div
     (if-let [options (seq (:options question))]
       [answer-options options questionnaire-id]
       [quick-buttons questionnaire-id])
     [ResponseEditor questionnaire-id]
     (when comment-required
       [CommentEditor questionnaire-id])
     analysis-divider
     [UnstructuredAnalysisEditor questionnaire-id]
     (when @(rf/subscribe [::common-subs/feature-enabled :applied-analysis-creation])
       [AnalysisEditor questionnaire-id current-question])]))

(defn question-status [{:keys [questionnaire-id id status selected-answer]}]
  (let [current-status status]
    [:div
     (map (fn [status]
            ^{:key (or status "unknown")}
            [question-status/status-button {:status status
                                            :current-status current-status
                                            :on-click (fn [_]
                                                        (if (= "inferred_document" (:source selected-answer))
                                                          (rf/dispatch [::notifications/add {:message "Please edit the Document search result first, by clicking \"edit as new\""
                                                                                             :class "has-background-danger"}])
                                                          (questionnaire.db/update-question-status
                                                           questionnaire-id
                                                           id
                                                           status)))
                                            :large? true
                                            :selected-answer selected-answer}])
          ["approved" "in-review" nil])]))

(defn save-controls [questionnaire-id]
  (let [question @(rf/subscribe [::subs/current-question questionnaire-id])
        editor-dirty? @(rf/subscribe [::subs/editor-dirty? questionnaire-id])]
    [:div
     [:div
      [notes-button {:style {:vertical-align "bottom"}} question]
      [attachments-button {:style {:vertical-align "bottom"}
                           :questionnaire-id questionnaire-id
                           :question-id (:id question)
                           :answer-id (-> question :selected-answer :id)}]
      (let [{:keys [response comment multi-select-options single-select-options]} @(rf/subscribe [::subs/edit-answer-state questionnaire-id])]
        [:button.button.is-rounded.ml-2.is-primary
         {:disabled (or (and (str/blank? response)
                             (str/blank? comment)
                             (empty? multi-select-options)
                             (empty? single-select-options))
                        (not editor-dirty?))
          :on-click (fn [_]
                      (rf/dispatch [::events/save-answer questionnaire-id question]))
          :title "Create a new Answer and set is as the Selected Answer for this question"}
         "Save"])]]))

(defn control-strip [questionnaire-id]
  (let [question @(rf/subscribe [::subs/current-question questionnaire-id])]
    [:div
     [:div.block.columns.is-vcentered.mt-4.mb-0
      [:div.column.is-narrow
       [question-status question]]
      [:div.column]
      [:div.column.is-narrow
       [save-controls questionnaire-id]]]
     ;; TODO: Forcing re-render of answer-scope/AnswerScope,
     ;;       since seems that the hook [answer-scope set-answer-scope] (react/useState initial-answer-scope)
     ;;       keeps the old value in answer-scope, while new is avaible in initial-answer-scope.
     ;;       Not sure if this is a reagent problem. - Kimmo K.
     ^{:key (-> question :answer-scope :content)}
     [:f> answer-scope/AnswerScope
      {:on-commit #(rf/dispatch [::answers/set-answer-scope questionnaire-id #{(:id question)} % "direct"])
       :initial-answer-scope (-> question :answer-scope :content)
       :tooltip-suffix (when-let [{:keys [author created]} (:answer-scope question)]
                         (str "Set by " author " at " (format-date created)))}]]))

(defn answer-key [{:keys [source]}]
  (cond
    (or (= "human" source)
        (= "inferred_faq" source))
    :answers

    (= "inferred_document" source)
    :documents

    ;; Err on the side of answers
    :else :answers))

(defn search-result-box [{:keys [selected :background-color]} result]
  [:div.mt-1
   {:style {:border (if selected
                      "2px solid"
                      "0.1em solid")
            :border-color (if selected
                            "#02585D"
                            "rgb(227,227,227)")
            :border-radius "0.3em"
            :background-color background-color}}
   result])

(defn response-class [response]
  (case (some-> response str/lower-case str/trim)
    "no" ["has-text-white" "has-background-danger"]
    "yes" ["has-text-white" "has-background-success"]
    "n/a" ["has-background-warning"]
    ["has-background-grey-lighter"]))

(defn info-modal [{{:keys [answer question]} :ctx}]
  (let [{:keys [source author]} answer
        search-result (-> answer :meta :faq)
        source-system (or (-> search-result :answer-data :source-system)
                          (:source-system answer))]
    [:div.card
     [:header.card-header.has-background-secondary.is-shadowless
      [:title.card-header-title "Details"]
      [:button.card-header-icon {:on-click #(rf/dispatch [::ui/pop-modal])}
       [:span.icon [:i.fas.fa-xmark]]]]
     [:div.card-content.p-1
      (when search-result
        (let [{:keys [questionnaire-name questionnaire-filename questionnaire-id]} search-result]
          (when questionnaire-id
            [:div.has-text-weight-bold.m-2
             [:span
              "Source questionnaire: "]
             [:a {:href (rfe/href question-list-config/view-id
                                  {:questionnaire-id questionnaire-id})
                  :target "blank_"}
              (or questionnaire-name questionnaire-filename)]])))
      (when (and source-system
                 (not= "webapp" source-system))
        (let [answer-data (or (-> search-result :answer-data)
                              answer)]
          [:div.has-text-weight-bold.m-2
           (str "Imported by "
                (or (:author search-result)
                    (:author answer-data))
                " at "
                (format-date (js/Date. (or (:created search-result)
                                           (:created answer))))
                " from "
                (cond
                  (= "questionnaire-import" source-system)
                  "Excel file"

                  (= "portal" source-system)
                  "Portal"

                  ;; TODO: Add friendly name for new sources
                  :else source-system))]))
      (when (and (= "human" source)
                 (= "webapp" source-system))
        [:div.has-text-weight-bold.m-2
         (str "Created by "
              author
              " at "
              (format-date (:created answer)))])
      (when (= "inferred_faq" source)
        [:div.has-text-weight-bold.m-2
         (str "Found by inference"
              " at "
              (format-date (:created answer)))])
      (when (and (= (:id answer) (-> question :selected-answer :id))
                 ;; Before 2023/11/14, the author data wasn't updated when answer was selected
                 (> (.getTime (-> question :selected-answer :selected-at))
                    (js/Date. 2023 10 14)))
        [:div.has-text-weight-bold.m-2
         [:span "Selected by "]
         (-> question :selected-answer :selected-by)
         " at "
         (format-date (-> question :selected-answer :selected-at))])]]))

(defn answer-item [questionnaire-id answer selected? current-question]
  (let [target-answer-id (or
                          ;; In case the answer links to existing answer
                          (some-> (-> answer :meta :faq :answer-id) parse-uuid)
                          ;; Otherwise this is a concrete answer
                          (:id answer))
        analysis-pred-fn (fn [applied-analysis]
                           (contains? (:answer-ids applied-analysis) target-answer-id))]
    (fn [_questionnaire-id _answer _selected? _current-question]
      (let [applied-analyses @(analysis.db/applied-analyses-for-answer* target-answer-id)
            {:keys [response comment analysis-content]} (common/answer->edit-state answer applied-analyses)]
        [:div.p-2
         [:div.columns.mb-0
          [:div.column.has-text-left.mt-2.ml-2
           [:div
            ;; Show question content when present, currently only from inference data
            (when-let [question-content (or (-> answer :meta :faq :content)
                                            (-> answer :meta :faq :question))]
              [:span.has-text-weight-bold
               (html/strip-markup question-content)])
            (when-not (str/blank? response)
              [:div.mt-1
               [:span.p-1
                {:style {:border-radius "0.3em"}
                 :class (response-class response)}
                response]])
            (when-not (str/blank? comment)
              [:div.mt-1
               comment])
            (when-let [selected-options (seq (filter :selected (if (= "human" (:source answer))
                                                                 (:options answer)
                                                                 (-> answer :meta :faq :question-options))))]
              [answer-options selected-options questionnaire-id nil])
            (when analysis-content
              [:div
               analysis-divider
               [:span analysis-content]])]]
          [:div.column.is-narrow.m-2
           [:a.button.is-rounded.is-small.is-secondary
            {:on-click (fn [e]
                         (.stopPropagation e)
                         (rf/dispatch [::events/set-edit-answer-state questionnaire-id (assoc (events/make-edit-state current-question answer applied-analyses)
                                                                                              :dirty? true)]))
             :title "Copy answer contents to editor panel"}
            [:span.icon
             [:i.fas.fa-copy]]]]]
         ;; Dont' show analyses for user's previously created answers to the current question,
         ;; since the answers point to the current question,
         ;; and the analyses linked are shown under the editor
         (when @(rf/subscribe [::common-subs/feature-enabled :applied-analysis-creation])
           (when target-answer-id
             (let [{:keys [applied-analysis-version-ids]} @(rf/subscribe [::subs/edit-answer-state questionnaire-id])]
               [LinkedAnalyses questionnaire-id
                analysis-pred-fn
                applied-analysis-version-ids false false])))
         [:div.level.p-2
          [:div.level-left
           [:div.level-item
            [:b.mr-2
             (cond
               (= "inferred_faq" (:source answer))
               [:span {:title "Similarity of the question text, when compared to the current question"}
                (str (format-score (-> answer :score)) " match")]

               (= "human" (:source answer))
               (:author answer)

               :else "")]
            [:span
             (format-date (if-let [source-created (-> answer :meta :faq :created)]
                            (js/Date. source-created)
                            (:created answer)))]]]
          [:div.level-right
           [:div.level-item
            [:a.button.is-small.is-rounded.is-circular.is-info
             {:on-click (fn [e]
                          (.stopPropagation e)
                          (rf/dispatch [::ui/push-modal {:component #'info-modal
                                                         :ctx {:answer answer
                                                               :question current-question}
                                                         :content-props {:style {:width "40vw"
                                                                                 :max-height "80vh"}}}]))
              :title "Show details, such as Answer source"}
             [:span.icon>i.fas.fa-info]]]
           (when-not selected?
             [:div.level-item
              [:a.button.is-primary.is-rounded.is-small
               {:on-click (fn [_]
                            (rf/dispatch [::events/select-answer questionnaire-id (:question-id answer) answer]))
                :title "Set answer as Selected Answer"}
               "Select"]])]]]))))

(defn document-item [questionnaire-id question answer]
  (let [applied-analyses @(analysis.db/applied-analyses-for-answer* (:id answer))]
    [:div
     [:div.columns.mb-0
      [:div.column.has-text-left.mt-2.ml-2
       (let [filename (or (-> answer :meta :name)
                          (-> answer :additional-information))]
         [:div
          [:a {:href "#" :on-click (fn [e]
                                     (.stopPropagation e)
                                     (rf/dispatch [::document-list/fetch-file-preview-url filename]))} filename]
          [:div.mt-2
           [common/render-document-answer answer]]])]
      [:div.column.is-narrow.m-2
       [:a.button.is-rounded.is-small
        {:on-click (fn [e]
                     (.stopPropagation e)
                     (rf/dispatch [::events/set-edit-answer-state questionnaire-id (assoc (events/make-edit-state question answer applied-analyses)
                                                                                          :dirty? true)]))
         :title "Copy answer contents to editor panel"}
        [:span.icon
         [:i.fas.fa-copy]]]]]
     [:div.level.p-2
      [:div.level-left
       [:div.level-item
        [:b.mr-2
         [:span {:title "Similarity of the question text to the chunk of text in the document"} (str (format-score (-> answer :score)) " match")]]
        [:span
         (format-date (:created answer))]]]]]))

(defn search-item-title [text]
  [:div.p-3.is-size-4.has-background-highlight
   {:style {:border-radius "0.3em"}}
   text])

(defn source-ordinal
  "Get a comparison ordinal for answer source, preferring humans."
  [source]
  (case source
    "human" 1
    -1))

(defn sort-answers
  "Sort answer based on score, creation time and author.

  Keeps answers authored by a human first, then ML results"
  [answers]
  (sort (fn [a b]
          (compare [(source-ordinal (:source b)) (-> b :score) (-> b :created)]
                   [(source-ordinal (:source a)) (-> a :score) (-> a :created)]))
        answers))

(defn search [questionnaire-id]
  (let [question @(rf/subscribe [::subs/current-question questionnaire-id])
        {answers :data} @(rf/subscribe [::answers/answers questionnaire-id (:id question)])
        selected-answer-id (-> question :selected-answer :id)
        {:keys [documents answers]} (group-by answer-key answers)]
    [:<>
      [:div.column.has-text-centered {:style {:overflow "scroll"}}
       [search-item-title "Answers"]
       [:div
        (map (fn [answer]
               (let [selected? (= selected-answer-id (:id answer))]
                 ^{:key (:id answer)}
                 [search-result-box
                  {:selected selected?
                   :background-color (when (and (= "human" (:source answer))
                                                (not selected?))
                                       "#FCFAF8")}
                  [answer-item questionnaire-id answer selected? question]]))
             (sort-answers answers))]]
      (let [docs (sort-by :score #(compare %2 %1) documents)]
        (when (seq docs)
          [:div.column.has-text-centered {:style {:overflow "scroll"}}
           [search-item-title "Documents"]
           [:div
            (map
             (fn [answer]
               ^{:key (:id answer)}
               [search-result-box {:selected (= selected-answer-id (:id answer))}
                [document-item questionnaire-id question answer]])
             docs)]]))]))

(defn question-lab [questionnaire-id]
  (let [index @(rf/subscribe [::subs/question-index questionnaire-id])
        total (count @(rf/subscribe [::subs/question-ids questionnaire-id]))
        show-prev-question #(when (> index 0)
                              (rf/dispatch [::events/set-question-index questionnaire-id (dec index) true]))
        show-next-question #(when (< index (- total 1))
                              (rf/dispatch [::events/set-question-index questionnaire-id (inc index) true]))]
    [:f> keybindings {:actions {"ArrowLeft" show-prev-question
                                "p" show-prev-question
                                "ArrowRight" show-next-question
                                "n" show-next-question}}
     [:div.columns.p-2 {:style {:height "100%"}}
      [:div.column {:style {:height "100%"
                            :overflow "scroll"}}
       [question-index questionnaire-id]
       [:div.p-4.my-4
        {:key (keyword :question index)
         :style {:border "2px solid"
                 :border-color "#02585D"
                 :border-radius "0.3em"}}
        [question-content questionnaire-id]
        [answering-inputs
         questionnaire-id
         @(rf/subscribe [::subs/current-question questionnaire-id])]
        [:f> control-strip questionnaire-id]]]
      [search questionnaire-id]]]))

(defn question-lab-modal [{{:keys [questionnaire-id]} :ctx}]
  (let [{:keys [custom-name filename]} @(rf/subscribe [::questionnaires/metadata questionnaire-id])]
    [panel/panel {:class ["is-secondary"]
                  :panel-class :has-background-white
                  :style {:width "100%" :height "100%"}
                  :title (str "Question Lab: " (or custom-name filename))
                  :actions [:<>
                            [:button.button.is-small.is-primary.is-rounded.ml-2 {:on-click #(rf/dispatch [::events/try-close-modal questionnaire-id])
                                                                                 :title "Close Question Lab and return to Question List View"}
                             [:span.icon [:i.fas.fa-xl.fa-xmark]]]]}
     [question-lab questionnaire-id]]))
